import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { addDays, differenceInDays, getYear, min, max, setYear } from 'date-fns';

// Collection name
const FARMING_HISTORY_CALCS_2_COLLECTION = 'pmt2_farminghistorycalcs2';

/**
 * Interface for Farming History Calcs 2 data
 */
export interface FarmingHistoryCalcs2Document {
  id: string; // ASIN
  asin: string;
  companyName: string;
  entries: FarmingHistoryCalcs2Entry[];
  [key: string]: any;
}

/**
 * Interface for a single entry in the Farming History Calcs 2 data
 */
export interface FarmingHistoryCalcs2Entry {
  date?: string;
  your_name?: string;
  data_element?: string;
  new_value?: string;
  clean_status?: string;
  period_start?: string;
  period_end?: string;
  days?: number;
  [key: string]: any;
}

/**
 * Helper function to safely parse a date string to a Date object
 * @param dateStr Date string (e.g., "3/1/25")
 * @returns Date object or null if parsing fails
 */
const parseDate = (dateStr: string | null | undefined): Date | null => {
  try {
    if (!dateStr) return null;
    
    // Parse date string (format: MM/DD/YY or MM/DD/YYYY)
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    
    const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);
    
    // Handle 2-digit years
    if (year < 100) {
      year += 2000; // Assume 20xx
    }
    
    const date = new Date(year, month, day);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    return null;
  }
};

/**
 * Helper function to calculate the overlap between two date ranges
 * @param rangeStart Start date of the first range
 * @param rangeEnd End date of the first range
 * @param seasonStart Start date of the second range
 * @param seasonEnd End date of the second range
 * @returns Number of overlapping days
 */
const calculateDateRangeOverlap = (
  rangeStart: Date,
  rangeEnd: Date,
  seasonStart: Date | null | undefined,
  seasonEnd: Date | null | undefined
): number => {
  // If season dates are missing or invalid, return 0 days
  if (!seasonStart || !seasonEnd || isNaN(seasonStart.getTime()) || isNaN(seasonEnd.getTime())) {
    return 0;
  }

  // Get all years in the range
  const startYear = getYear(rangeStart);
  const endYear = getYear(rangeEnd);
  const years: number[] = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }

  // Calculate overlap for each year and sum
  let totalOverlapDays = 0;
  for (const year of years) {
    // Create season dates for this year
    const yearSeasonStart = setYear(new Date(seasonStart), year);
    const yearSeasonEnd = setYear(new Date(seasonEnd), year);

    // Calculate overlap
    const overlapStart = max([rangeStart, yearSeasonStart]);
    const overlapEnd = min([rangeEnd, yearSeasonEnd]);

    // Check if there's a valid overlap
    if (overlapStart <= overlapEnd) {
      // Add 1 to include both start and end dates (inclusive)
      const overlapDays = differenceInDays(overlapEnd, overlapStart) + 1;
      totalOverlapDays += overlapDays;
    }
  }

  return totalOverlapDays;
};

/**
 * Fetches Farming History Calcs 2 data for a specific ASIN
 * @param companyName Company name
 * @param asin ASIN
 * @returns The Farming History Calcs 2 document for the ASIN
 */
export const getFarmingHistoryCalcs2Data = async (
  companyName: string,
  asin: string
): Promise<FarmingHistoryCalcs2Document | null> => {
  try {
    // Get the document reference
    const docRef = doc(db, FARMING_HISTORY_CALCS_2_COLLECTION, companyName, 'entries', asin);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        companyName,
        ...data,
        // Ensure entries is an array
        entries: Array.isArray(data.entries) ? data.entries : []
      } as FarmingHistoryCalcs2Document;
    }

    return null;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches all Farming History Calcs 2 data for a company
 * @param companyName Company name
 * @returns Array of Farming History Calcs 2 documents
 */
export const getAllFarmingHistoryCalcs2Data = async (
  companyName: string
): Promise<FarmingHistoryCalcs2Document[]> => {
  try {
    // Get the collection reference
    const collectionRef = collection(db, FARMING_HISTORY_CALCS_2_COLLECTION, companyName, 'entries');
    const querySnapshot = await getDocs(collectionRef);

    const documents: FarmingHistoryCalcs2Document[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      documents.push({
        id: doc.id,
        companyName,
        ...data,
        // Ensure entries is an array
        entries: Array.isArray(data.entries) ? data.entries : []
      } as FarmingHistoryCalcs2Document);
    });

    return documents;
  } catch (error) {
    throw error;
  }
};

/**
 * Calculates the days farming for a specific ASIN
 * and date range
 * using all farming entries
 from the GPO Performance table
 * @param startDate Start date of the GPO period
 * @param endDate End date of the GPO period
 * @param allFarmingDates Array of all farming date pairs from the GPO Performance table
 * @returns The number of days farming or null if not found
 */
export const calculateDaysFarming = async (
  startDate: Date,
  endDate: Date,
  allFarmingDates: { start: Date | null, end: Date | null }[]
): Promise<number | null> => {
  try {
    // If there are no farming dates, return 0
    if (allFarmingDates.length === 0) {
      return 0;
    }
    
    // Calculate total farming days within the GPO period
    let totalFarmingDays = 0;
    
    for (const { start: periodStart, end: periodEnd } of allFarmingDates) {

      // If period_start is missing, skip this entry
      if (!periodStart) {
        continue;
      }
      
      // Calculate the overlap between the GPO period and the farming period
      const overlapDays = calculateDateRangeOverlap(
        startDate,
        endDate,
        periodStart,
        periodEnd || new Date() // If period_end is missing, use today's date
      );
      
      totalFarmingDays += overlapDays;
    }
    
    return totalFarmingDays;
  } catch (error) {
    return 0; // Return 0 instead of null on error for consistency
  }
};